import {
  Button,
  Checkbox,
  Collapse,
  Icon,
  Radio,
  RadioGroup,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import * as React from 'react';
import { ItemSelector, Text, Tooltip } from 'vet-bones/components';
import { SelectItem } from 'vet-bones/utils/select';

import { InlineEditableLabel } from 'src/components/InlineEditableLabel';
import {
  TEST_IDS,
  getNamedGraphItemTestId,
  getPublishProjectDrawerCreationTypeTestId,
  getPublishProjectDrawerShouldPublishCheckboxTestId,
} from 'src/constants/testIds';
import {
  Mapping,
  MappingIdentifierType,
  RelationshipMapping,
} from 'src/containers/ProjectDiagram/types/data';
import { FileUploadInput } from 'src/containers/PublishProjectDrawer/FileUploadInput';
import {
  useCsvNamedGraphIriPlaceholder,
  useFindNamedGraph,
  useValidateNamedGraphIri,
} from 'src/containers/PublishProjectDrawer/hooks';
import {
  CreationType,
  CsvMappingInfo,
  ModelsInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { connectionSelector } from 'src/selectors/connection';
import { dataModelItemsSelector } from 'src/selectors/dataModelItems';
import { isStardogFeatureSupportedSelector } from 'src/selectors/isStardogFeatureSupported';
import { namedGraphItemsSelector } from 'src/selectors/namedGraphItems';
import { projectSelector } from 'src/selectors/project';
import { ProjectFileUpload } from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export type CSVMappingRowProps = {
  databaseId: string;
  fileUpload: ProjectFileUpload;
  info: CsvMappingInfo;
  initialInfo: CsvMappingInfo;
  mappings: Mapping[];
  modelsInfo: ModelsInfo;
  relationshipMappings: RelationshipMapping[];
  setInfo: (info: CsvMappingInfo) => void;
  testId?: string;
};

export const CSVMappingRow = ({
  databaseId,
  fileUpload,
  info,
  initialInfo,
  mappings,
  modelsInfo,
  relationshipMappings,
  setInfo,
  testId,
}: CSVMappingRowProps) => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));

  const didFileUploadChange = useDidValueChange(fileUpload);
  const findNamedGraph = useFindNamedGraph(databaseId);
  const validateNamedGraphIri = useValidateNamedGraphIri(
    databaseId,
    undefined,
    undefined,
    modelsInfo
  );
  const namedGraphIriPlaceholder = useCsvNamedGraphIriPlaceholder(
    databaseId,
    fileUpload
  );
  const dataModelItems = useReduxSelector(dataModelItemsSelector(databaseId));
  const allNamedGraphSelectItems = useReduxSelector(
    namedGraphItemsSelector(databaseId)
  );
  const namedGraphSelectItems = allNamedGraphSelectItems.filter(
    ({ value: namedGraphIri }) =>
      !dataModelItems.find(
        (dataModel) => dataModel.namedGraphIri === namedGraphIri
      )
  );
  const connection = useReduxSelector(connectionSelector);
  const isStaticIriRelationshipMappingSupported = useReduxSelector(
    isStardogFeatureSupportedSelector(
      'isStaticIriRelationshipMappingSupported',
      connection
    )
  );

  const [isOpen, setIsOpen] = React.useState(false);

  const initialReplaceNamedGraph =
    initialInfo.creationType === CreationType.NEW
      ? null
      : findNamedGraph(initialInfo.namedGraphIri) || null;
  const [replace, setReplace] = React.useState<SelectItem | null>(
    initialReplaceNamedGraph
  );

  const initialAppendNamedGraph =
    initialInfo.creationType === CreationType.NEW
      ? null
      : findNamedGraph(initialInfo.namedGraphIri) || null;
  const [append, setAppend] = React.useState<SelectItem | null>(
    initialAppendNamedGraph
  );

  React.useEffect(() => {
    if (didFileUploadChange) {
      setReplace(initialReplaceNamedGraph);
      setAppend(initialAppendNamedGraph);
      const expand = Boolean(
        initialInfo.creationType === CreationType.NEW &&
          validateNamedGraphIri({ ...initialInfo, modelName: '' })
      );
      setIsOpen(expand);
    }
  }, [
    didFileUploadChange,
    fileUpload,
    initialAppendNamedGraph,
    initialInfo,
    initialReplaceNamedGraph,
    project,
    validateNamedGraphIri,
  ]);

  const handleSetInfo = React.useCallback(
    (nextInfo: Partial<CsvMappingInfo>) => {
      setInfo({ ...info, ...nextInfo });
    },
    [info, setInfo]
  );

  const { creationType, namedGraphIri, fileOptions } = info;

  const handleSelect = React.useCallback(
    (existing: SelectItem | null) => {
      if (creationType === CreationType.REPLACE) {
        setReplace(existing);
      } else if (creationType === CreationType.APPEND) {
        setAppend(existing);
      }
      if (existing?.value) {
        handleSetInfo({ namedGraphIri: existing.value });
      }
    },
    [creationType, handleSetInfo]
  );

  const handleChangeCreationType = React.useCallback(
    ({ currentTarget: { value } }: React.FormEvent<HTMLInputElement>) => {
      const newCreationType = value as CreationType;
      if (newCreationType === CreationType.NEW) {
        handleSetInfo({
          creationType: newCreationType,
          namedGraphIri: namedGraphIriPlaceholder,
        });
        if (!namedGraphIriPlaceholder) {
          setIsOpen(true);
        }
      } else if (newCreationType === CreationType.REPLACE) {
        handleSetInfo({
          creationType: newCreationType,
          namedGraphIri: replace?.value || '',
        });
      } else if (newCreationType === CreationType.APPEND) {
        handleSetInfo({
          creationType: newCreationType,
          namedGraphIri: append?.value || '',
        });
      }
    },
    [replace, append, handleSetInfo, namedGraphIriPlaceholder]
  );

  const onValidate = (namedGraphIri: string) => {
    if (
      creationType === CreationType.REPLACE ||
      creationType === CreationType.APPEND
    ) {
      return '';
    }
    return validateNamedGraphIri({
      creationType,
      namedGraphIri,
      modelName: '',
    });
  };

  let selectorTestId;
  if (creationType === CreationType.REPLACE) {
    selectorTestId = TEST_IDS.namedGraph.replace.selector;
  } else if (creationType === CreationType.APPEND) {
    selectorTestId = TEST_IDS.namedGraph.append.selector;
  }

  let selectButtonText = '';
  if (creationType === CreationType.REPLACE) {
    selectButtonText = replace?.text || '';
  } else if (creationType === CreationType.APPEND) {
    selectButtonText = append?.text || '';
  }

  const errors: string[] = [];

  if (
    !isStaticIriRelationshipMappingSupported &&
    relationshipMappings.some(
      (mapping) =>
        (mapping.fromPrimaryIdentifier &&
          !mapping.fromPrimaryIdentifier.parts.some(
            (part) => part.type === MappingIdentifierType.FIELD
          )) ||
        (mapping.toPrimaryIdentifier &&
          !mapping.toPrimaryIdentifier.parts.some(
            (part) => part.type === MappingIdentifierType.FIELD
          ))
    )
  ) {
    errors.push(
      copy.components.publishProjectDrawer.mappingConfiguration.virtualGraph
        .staticIriWarning
    );
  }

  const disabled = !info.shouldPublish;

  return (
    <div className="sd-resource-mapping-row" data-testid={testId}>
      <div className="sd-resource-mapping-row__body">
        <div className="sd-resource-mapping-grid">
          <div className="sd-resource-mapping-grid__should-publish">
            <Checkbox
              checked={info.shouldPublish}
              data-testid={getPublishProjectDrawerShouldPublishCheckboxTestId(
                fileUpload.id
              )}
              label={undefined}
              onChange={({ currentTarget: { checked } }) => {
                handleSetInfo({ shouldPublish: checked });
                if (!checked) {
                  setIsOpen(false);
                }
              }}
            />
          </div>
          <div className="sd-resource-mapping-grid__resource-name">
            <Text ellipsize>{fileUpload.name}</Text>
            {errors.length ? (
              <Tooltip content={errors.join('\n\n')}>
                <Icon icon={IconNames.WARNING_SIGN} />
              </Tooltip>
            ) : null}
          </div>
          <div className="sd-resource-mapping-row__mappings">
            {mappings.map(({ key, text }) => (
              <Text key={key} ellipsize>
                {text}
              </Text>
            ))}
          </div>
          <RadioGroup
            disabled={disabled}
            inline
            onChange={handleChangeCreationType}
            selectedValue={creationType}
          >
            <Radio
              data-testid={getPublishProjectDrawerCreationTypeTestId(
                CreationType.NEW
              )}
              disabled={disabled}
              label={
                copy.components.publishProjectDrawer.mappingConfiguration
                  .namedGraph.create
              }
              value={CreationType.NEW}
            />
            <Radio
              data-testid={getPublishProjectDrawerCreationTypeTestId(
                CreationType.REPLACE
              )}
              disabled={disabled}
              labelElement={
                copy.components.publishProjectDrawer.mappingConfiguration
                  .namedGraph.replace
              }
              value={CreationType.REPLACE}
            />
            <Radio
              data-testid={getPublishProjectDrawerCreationTypeTestId(
                CreationType.APPEND
              )}
              disabled={disabled}
              labelElement={
                copy.components.publishProjectDrawer.mappingConfiguration
                  .namedGraph.append
              }
              value={CreationType.APPEND}
            />
            <div className="sd-drawer-selector-menu">
              <ItemSelector.Single
                buttonText={selectButtonText}
                disabled={creationType === CreationType.NEW || disabled}
                filterText={copy.components.defaults.filter}
                items={namedGraphSelectItems}
                itemTestId={getNamedGraphItemTestId}
                onSelect={handleSelect}
                selected={replace}
                testId={selectorTestId}
              />
            </div>
          </RadioGroup>
          <div className="sd-resource-mapping-row__collapse-button">
            <Button
              data-testid={TEST_IDS.publishProjectDrawer.mapping.collapseButton}
              disabled={disabled}
              icon={isOpen ? IconNames.CHEVRON_UP : IconNames.CHEVRON_DOWN}
              minimal
              onClick={() => setIsOpen(!isOpen)}
            />
          </div>
        </div>
      </div>

      {fileUpload.partial ? (
        <div
          className="sd-resource-mapping-row__upload"
          data-testid={TEST_IDS.publishProjectDrawer.mapping.upload}
        >
          <Text>
            {
              copy.components.publishProjectDrawer.mappingConfiguration
                .csvUpload.label
            }
          </Text>
          <Tooltip
            content={
              copy.components.publishProjectDrawer.mappingConfiguration
                .csvUpload.info
            }
          >
            <Icon icon={IconNames.INFO_SIGN} />
          </Tooltip>

          <FileUploadInput
            disabled={disabled}
            fileOptions={fileOptions}
            fileUpload={fileUpload}
            setFileOptions={(nextFileOptions) =>
              handleSetInfo({ fileOptions: nextFileOptions })
            }
          />
        </div>
      ) : null}

      <Collapse isOpen={isOpen}>
        <div
          className="sd-resource-mapping-row__collapse-body"
          data-testid={TEST_IDS.publishProjectDrawer.mapping.collapseBody}
        >
          <InlineEditableLabel
            disabled={
              creationType === CreationType.REPLACE ||
              creationType === CreationType.APPEND ||
              disabled
            }
            label={
              copy.components.publishProjectDrawer.mappingConfiguration
                .namedGraph.label
            }
            onSetValue={(nextNamedGraphIri) =>
              handleSetInfo({ namedGraphIri: nextNamedGraphIri })
            }
            onValidate={onValidate}
            placeholder={namedGraphIriPlaceholder}
            testId={TEST_IDS.publishProjectDrawer.mapping.csvNamedGraphInput}
            value={
              (creationType === CreationType.REPLACE && !replace) ||
              (creationType === CreationType.APPEND && !append)
                ? copy.components.publishProjectDrawer.mappingConfiguration
                    .namedGraph.placeholder
                : namedGraphIri
            }
          />
        </div>
      </Collapse>
    </div>
  );
};
